import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useTour } from '@reactour/tour';

import { useTourContext } from '@/shared/lib/context';
import { isTourSeen } from '@/shared/lib/tour';
import type { TourStepConfig, UsePageTourConfig } from '@/shared/types';

export function usePageTour(configInput: UsePageTourConfig): void {
    const config = useMemo(() => configInput, [configInput]);
    const { namespace, steps: stepsInput, welcomeModal, version } = config;
    const { t } = useTranslation(namespace);
    const { setCurrentStep, setIsOpen, setSteps } = useTour();
    const { openWelcomeModalAuto, setModal, setOpenWelcomeModal } =
        useTourContext();
    const { pathname } = useLocation();

    const steps = useMemo(
        () =>
            stepsInput.map((step) => ({
                content: t(step.contentKey),
                selector: step.selector === false ? 'body' : step.selector,
                title: t(step.titleKey),
                ...(step.action ? { action: step.action } : {}),
                ...(step.resizeObservables
                    ? { resizeObservables: step.resizeObservables }
                    : {}),
            })) as TourStepConfig[],
        [stepsInput, t],
    );

    useEffect(() => {
        if (setSteps) {
            setSteps(steps);
        }

        return () => {
            if (setSteps) {
                setSteps([]);
            }
        };
    }, [setSteps, steps]);

    const handleCloseWelcomeModal = useCallback(() => {
        setModal(null);
    }, [setModal]);

    const handleStartTour = useCallback(() => {
        if (setCurrentStep && setIsOpen) {
            setIsOpen(true);
            setCurrentStep(0);
        }
    }, [setCurrentStep, setIsOpen]);

    const handleOpenWelcomeModal = useCallback(() => {
        if (!welcomeModal || steps.length === 0) {
            return;
        }

        setModal({
            config: welcomeModal,
            isOpen: true,
            startHandler: () => {
                setModal(null);

                setTimeout(() => {
                    handleStartTour();
                }, 300);
            },
            closeHandler: handleCloseWelcomeModal,
        });
    }, [
        handleCloseWelcomeModal,
        handleStartTour,
        setModal,
        steps.length,
        welcomeModal,
    ]);

    useEffect(() => {
        setOpenWelcomeModal(() => handleOpenWelcomeModal);

        return () => {
            setOpenWelcomeModal(null);
        };
    }, [handleOpenWelcomeModal, setOpenWelcomeModal]);

    useEffect(() => {
        if (!welcomeModal || steps.length === 0) {
            return;
        }

        if (isTourSeen(pathname, version)) {
            return;
        }

        openWelcomeModalAuto({
            config: welcomeModal,
            pagePath: pathname,
            version,
            startHandler: handleStartTour,
            closeHandler: handleCloseWelcomeModal,
        });
    }, [
        handleCloseWelcomeModal,
        handleStartTour,
        openWelcomeModalAuto,
        pathname,
        steps.length,
        version,
        welcomeModal,
    ]);
}
